// functions/src/utils/urlHelper.ts

import {
    decodeUrlRepeatedly,
    decodeHtmlEntities,
    containsDangerousSchemes
} from './urlValidationShared'
export { decodeUrlRepeatedly, decodeHtmlEntities, containsDangerousSchemes }

/**
 * URLのハッシュ（フラグメント）部分が安全かどうか検証する。
 */
export function isHashSafe(hash: string): boolean {
    if (!hash) {
        return true
    }
    const { decoded, success } = decodeUrlRepeatedly(hash)
    if (!success) {
        return false
    }
    const decodedHtml = decodeHtmlEntities(decoded)
    return (
        !containsDangerousSchemes(decoded) &&
        !containsDangerousSchemes(decodedHtml)
    )
}

/**
 * URL文字列をトリムし、不正なコントロール文字を検知する。
 */
export function cleanAndNormalizeUrl(
    url: string | null | undefined
): string | null {
    if (typeof url !== 'string') {
        return null
    }
    const trimmed = url.trim()
    if (/[\x00-\x1F\x7F-\x9F]/.test(trimmed)) {
        return null
    }
    return trimmed
}

/**
 * URLオブジェクトをパースし、安全なプロトコル（http/https）であることを検証する。
 */
export function parseAndValidateProtocolCleaned(cleaned: string): URL | null {
    try {
        const parsed = new URL(cleaned)
        if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
            return parsed
        }
        return null
    } catch (e) {
        return null
    }
}

/**
 * 単一のクエリパラメータ（キーまたは値）が安全であるか検証する。
 */
function validateQueryParamPart(
    paramPart: string,
    allowedHostCheckFn:
        ((hostname: string, protocol: string) => boolean) | undefined,
    depth: number
): boolean {
    const { decoded, success } = decodeUrlRepeatedly(paramPart)
    if (!success) {
        return false
    }
    const trimmedDecoded = decoded.trim()
    const lowerValue = trimmedDecoded.toLowerCase()
    const normalizedValue = lowerValue.replace(/\\/g, '/')
    const decodedHtml = decodeHtmlEntities(normalizedValue)
    if (
        decodedHtml.startsWith('http://') ||
        decodedHtml.startsWith('https://') ||
        decodedHtml.startsWith('//') ||
        decodedHtml.startsWith('\\\\') ||
        /^[a-z0-9+.-]+:/i.test(decodedHtml)
    ) {
        if (allowedHostCheckFn) {
            if (
                !isSafeRedirectUrlInternal(
                    trimmedDecoded,
                    allowedHostCheckFn,
                    depth + 1
                )
            ) {
                return false
            }
        } else {
            // コールバックがない場合は相対パス内の外部URLは一律拒否
            return false
        }
    }
    if (
        containsDangerousSchemes(trimmedDecoded) ||
        containsDangerousSchemes(decodedHtml)
    ) {
        return false
    }
    return true
}

/**
 * クエリパラメータの再帰検証を行うヘルパー関数。
 */
function validateQueryParams(
    parsed: URL,
    allowedHostCheckFn:
        ((hostname: string, protocol: string) => boolean) | undefined,
    depth: number
): boolean {
    const MAX_QUERY_PARAMS = 100
    const params = parsed.searchParams

    // パラメータ数上限チェックでDoSを防止
    if (Array.from(params.keys()).length > MAX_QUERY_PARAMS) {
        return false
    }

    for (const [key, value] of params.entries()) {
        if (!validateQueryParamPart(key, allowedHostCheckFn, depth)) {
            return false
        }
        if (!validateQueryParamPart(value, allowedHostCheckFn, depth)) {
            return false
        }
    }
    return true
}

/**
 * パースされたURLがローカルホスト（ポートなし、httpスキーム、ユーザー情報なし）としての構造を満たしているかを検証する。
 */
function validateLocalUrlStructure(parsed: URL): boolean {
    if (parsed.hostname !== 'localhost' || parsed.port !== '') {
        return false
    }
    if (parsed.protocol !== 'http:') {
        return false
    }
    if (parsed.username || parsed.password) {
        return false
    }
    return true
}

/**
 * デコード結果にダブルエンコード表現やバックスラッシュ、コントロール文字が含まれるかチェックする。
 */
function hasDoubleEncodingOrUnsafeChars(decoded: string): boolean {
    if (/%[0-9a-fA-F]{2}/.test(decoded)) {
        return true
    }
    const trimmed = decoded.trim()
    if (trimmed.includes('\\')) {
        return true
    }
    if (/[\x00-\x1F\x7F-\x9F]/.test(trimmed)) {
        return true
    }
    return false
}

/**
 * 対象が相対パスである場合に、それが安全か判定する。
 * 相対パスでない（絶対URLである）場合は null を返す。
 */
function checkRelativePathRedirect(
    trimmedDecoded: string,
    allowedHostCheckFn: (hostname: string, protocol: string) => boolean,
    depth: number
): boolean | null {
    if (
        trimmedDecoded.startsWith('/') &&
        !trimmedDecoded.startsWith('//') &&
        !trimmedDecoded.startsWith('/\\')
    ) {
        return isSafeRelativeUrlDecoded(
            trimmedDecoded,
            allowedHostCheckFn,
            depth
        )
    }
    // 追加ガード: バックスラッシュで始まる不正な相対パス表現を明示的に拒否
    if (trimmedDecoded.startsWith('\\') || trimmedDecoded.startsWith('/\\')) {
        return false
    }
    return null
}

/**
 * Authority部分における難読化バイパスをチェックする。
 */
function validateAuthority(trimmedDecoded: string): boolean {
    const doubleSlashIndex = trimmedDecoded.indexOf('://')
    const start =
        doubleSlashIndex !== -1
            ? doubleSlashIndex + 3
            : trimmedDecoded.indexOf(':') + 1
    let end = trimmedDecoded.length
    for (let i = start; i < trimmedDecoded.length; i++) {
        const char = trimmedDecoded[i]
        if (char === '/' || char === '?' || char === '#') {
            end = i
            break
        }
    }
    const authority = trimmedDecoded.slice(start, end)
    const decodedAuthority = decodeHtmlEntities(authority)
    if (decodedAuthority.includes('@') || decodedAuthority.includes('\\')) {
        return false
    }
    // Unicode/IDN ドメインの検証 (ASCII のみ許可)
    if (/[^\x00-\x7F]/.test(authority)) {
        return false
    }
    return true
}

/**
 * 絶対URLオブジェクト（parsed）の追加検証（userinfo、ホスト・プロトコル、ハッシュ、クエリパラメータ）を行う。
 */
function validateAbsoluteUrlParsed(
    parsed: URL,
    allowedHostCheckFn: (hostname: string, protocol: string) => boolean,
    depth: number
): boolean {
    if (parsed.username || parsed.password) {
        return false
    }
    if (!allowedHostCheckFn(parsed.hostname, parsed.protocol)) {
        return false
    }
    if (!isHashSafe(parsed.hash)) {
        return false
    }
    if (!validateQueryParams(parsed, allowedHostCheckFn, depth)) {
        return false
    }
    return true
}

/**
 * デコード済みの相対URLが安全かどうか検証する。
 */
export function isSafeRelativeUrlDecoded(
    decodedUrl: string,
    allowedHostCheckFn?: (hostname: string, protocol: string) => boolean,
    depth = 0
): boolean {
    const cleaned = cleanAndNormalizeUrl(decodedUrl)
    if (cleaned === null) {
        return false
    }

    if (
        !cleaned.startsWith('/') ||
        cleaned.startsWith('//') ||
        cleaned.startsWith('/\\')
    ) {
        return false
    }

    try {
        const parsed = new URL(cleaned, 'http://localhost')
        if (!validateLocalUrlStructure(parsed)) {
            return false
        }

        if (!isHashSafe(parsed.hash)) {
            return false
        }

        // クエリパラメータの再帰検証
        if (!validateQueryParams(parsed, allowedHostCheckFn, depth)) {
            return false
        }

        return true
    } catch (e) {
        return false
    }
}

/**
 * 内部用のリダイレクト検証ロジック。
 */
export function isSafeRedirectUrlInternal(
    url: string | null | undefined,
    allowedHostCheckFn: (hostname: string, protocol: string) => boolean,
    depth = 0
): boolean {
    if (depth > 3) {
        return false
    }
    const cleaned = cleanAndNormalizeUrl(url)
    if (cleaned === null) {
        return false
    }

    // ダブルエンコードおよび不正文字の検証
    const { decoded, success } = decodeUrlRepeatedly(cleaned)
    if (!success || hasDoubleEncodingOrUnsafeChars(decoded)) {
        return false
    }

    // HTMLエンティティ難読化による危険スキームの事前排除
    const decodedHtml = decodeHtmlEntities(decoded)
    if (containsDangerousSchemes(decodedHtml)) {
        return false
    }

    const trimmedDecoded = decoded.trim()

    // 相対パスの検証
    const relativeCheck = checkRelativePathRedirect(
        trimmedDecoded,
        allowedHostCheckFn,
        depth
    )
    if (relativeCheck !== null) {
        return relativeCheck
    }

    // 絶対URLの検証
    const parsed = parseAndValidateProtocolCleaned(trimmedDecoded)
    if (parsed === null) {
        return false
    }

    // authorityの難読化チェック
    if (!validateAuthority(trimmedDecoded)) {
        return false
    }

    // URLオブジェクトとしての安全性チェック
    if (!validateAbsoluteUrlParsed(parsed, allowedHostCheckFn, depth)) {
        return false
    }

    return true
}

/**
 * 安全な絶対URL(http/https)かどうかを検証する。危険なスキームを排除。
 */
export function isSafeAbsoluteUrl(url: string | null | undefined): boolean {
    const cleaned = cleanAndNormalizeUrl(url)
    if (cleaned === null) {
        return false
    }
    const { decoded, success } = decodeUrlRepeatedly(cleaned)
    if (!success) {
        return false
    }
    const decodedHtml = decodeHtmlEntities(decoded)
    // 絶対URLのプロトコルは下の new URL() で http/https に制約するため、ここでは
    // スキームの「先頭一致」だけを危険判定する（パス/クエリ内の "data:" 等の誤検知を防ぐ）。
    if (containsDangerousSchemes(decodedHtml, { startsWithOnly: true })) {
        return false
    }
    try {
        const parsed = new URL(cleaned)
        return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch (e) {
        return false
    }
}
